import React, { useState, useEffect } from "react";
import { useApiCarbonScoresList } from "@/api/lctApIComponents";
import { Pvp } from "@/api/lctApISchemas";
import { useAppSelector } from "@/hooks/reduxHooks";
import { CircleCheck, Scale, TrendingDown, TrendingUp } from "lucide-react";
import { DateTime } from "luxon";
import { CarbonScoreMetric } from "../CarbonScoreMetric";

interface IProps {
  pvp: Pvp;
}

type CarbonScoreMetric = {
  score?: number | null;
  date?: DateTime | null;
};

const defaultCarbonScoreMetric: CarbonScoreMetric = {
  score: null,
  date: null,
};

export const CarbonScoreMetrics: React.FC<IProps> = ({ pvp }) => {
  const token = useAppSelector((state) => state.user.authKey);
  const [latestCarbonScore, setLatestCarbonScore] = useState<CarbonScoreMetric | null>(defaultCarbonScoreMetric);
  const [carbonScoreAverage, setCarbonScoreAverage] = useState<CarbonScoreMetric | null>(defaultCarbonScoreMetric);
  const [lowestCarbonScore, setLowestCarbonScore] = useState<CarbonScoreMetric | null>(defaultCarbonScoreMetric);
  const [highestCarbonScore, setHighestCarbonScore] = useState<CarbonScoreMetric | null>(defaultCarbonScoreMetric);
  const [displayScores, setDisplayScores] = useState<boolean>(false);
  const { data: carbonScoreData, isLoading: isLoadingCarbonScore } = useApiCarbonScoresList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { application__pvp: pvp.id },
    },
    { enabled: !!token },
  );

  useEffect(() => {
    setDisplayScores(false);
    if (!carbonScoreData?.results || carbonScoreData?.count === 0) {
      return;
    }

    let totalScore = 0;
    let totalCount = 0;

    carbonScoreData?.results.forEach((carbonScore) => {
      const date = DateTime.fromISO(carbonScore.updated_at);
      if (latestCarbonScore?.date === null || (latestCarbonScore?.date && date.toMillis() > latestCarbonScore.date!.toMillis())) {
        setLatestCarbonScore({ score: carbonScore.carbon_score, date: date });
      }
      if (lowestCarbonScore?.score === null || (lowestCarbonScore?.score && (carbonScore.carbon_score ?? 0) < lowestCarbonScore.score!)) {
        setLowestCarbonScore({ score: carbonScore.carbon_score, date: date });
      }
      if (
        highestCarbonScore?.score === null ||
        (highestCarbonScore?.score && (carbonScore.carbon_score ?? 0) > highestCarbonScore.score!)
      ) {
        setHighestCarbonScore({ score: carbonScore.carbon_score, date: date });
      }
      totalScore += carbonScore.carbon_score ?? 0;
      totalCount++;
    });

    setCarbonScoreAverage({ score: totalScore / totalCount, date: null });
    setDisplayScores(true);
  }, [carbonScoreData, latestCarbonScore, lowestCarbonScore, highestCarbonScore]);

  return (
    <div className="mt-6">
      <h2 className="mb-2 w-full text-2xl font-semibold">{`${pvp.title} Carbon Score`}</h2>
      <div className="flex flex-wrap gap-4">
        <CarbonScoreMetric
          statValue={displayScores ? (latestCarbonScore?.score ?? "N/A") : "N/A"}
          metricTitle="Latest Approved Carbon Score"
          icon={CircleCheck}
          isLoading={isLoadingCarbonScore}
          subTitle={displayScores && latestCarbonScore?.date ? `from ${latestCarbonScore.date.toFormat("MM/dd/yyyy")} ` : undefined}
        />
        <CarbonScoreMetric
          statValue={displayScores ? (Math.round(carbonScoreAverage?.score ?? 0) ?? "N/A") : "N/A"}
          metricTitle="Carbon Score Average"
          icon={Scale}
          isLoading={isLoadingCarbonScore}
          subTitle={displayScores ? "from approved applications" : undefined}
        />
        <CarbonScoreMetric
          statValue={displayScores ? (lowestCarbonScore?.score ?? "N/A") : "N/A"}
          metricTitle="Lowest Approved Carbon Score"
          icon={TrendingDown}
          isLoading={isLoadingCarbonScore}
          subTitle={displayScores && lowestCarbonScore?.date ? `from ${lowestCarbonScore.date.toFormat("MM/dd/yyyy")} ` : undefined}
        />
        <CarbonScoreMetric
          statValue={displayScores ? (highestCarbonScore?.score ?? "N/A") : "N/A"}
          metricTitle="Highest Approved Carbon Score"
          icon={TrendingUp}
          isLoading={isLoadingCarbonScore}
          subTitle={displayScores && highestCarbonScore?.date ? `from ${highestCarbonScore.date.toFormat("MM/dd/yyyy")} ` : undefined}
        />
      </div>
    </div>
  );
};
